import { cn } from "@/lib/utils"

export function Skeleton({ className, ...props }) {
    return (
        <div
            className={cn(
                "animate-pulse rounded-md bg-slate-200/80 dark:bg-slate-700/50",
                className
            )}
            {...props}
        />
    )
}

/** Skeleton que simula el banner hero del kanban */
export function HeroBannerSkeleton() {
    return (
        <div className="relative w-full rounded-2xl overflow-hidden bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 dark:from-slate-800 dark:via-slate-700 dark:to-slate-800 h-44 sm:h-52 animate-pulse">
            <div className="absolute inset-0 p-6 flex flex-col justify-end gap-3">
                <Skeleton className="h-5 w-40 bg-slate-300/60 dark:bg-slate-600/60" />
                <Skeleton className="h-8 w-64 bg-slate-300/60 dark:bg-slate-600/60" />
                <div className="flex gap-4 mt-2">
                    <Skeleton className="h-10 w-28 rounded-xl bg-slate-300/60 dark:bg-slate-600/60" />
                    <Skeleton className="h-10 w-28 rounded-xl bg-slate-300/60 dark:bg-slate-600/60" />
                    <Skeleton className="h-10 w-28 rounded-xl bg-slate-300/60 dark:bg-slate-600/60" />
                </div>
            </div>
        </div>
    )
}

/** Skeleton que simula una tarjeta de lead en el kanban */
export function LeadCardSkeleton() {
    return (
        <div className="rounded-xl border border-slate-200/70 dark:border-slate-700/50 bg-white dark:bg-slate-900/60 p-3 space-y-2.5">
            <div className="flex items-center gap-2">
                <Skeleton className="h-7 w-7 rounded-full flex-shrink-0" />
                <Skeleton className="h-3.5 w-24 flex-1" />
                <Skeleton className="h-5 w-5 rounded-md flex-shrink-0" />
            </div>
            <Skeleton className="h-2.5 w-32" />
            <Skeleton className="h-2.5 w-20" />
            <div className="flex items-center justify-between pt-1">
                <Skeleton className="h-4 w-16 rounded" />
                <Skeleton className="h-5 w-12 rounded" />
            </div>
        </div>
    )
}

/** Skeleton que simula una columna completa del kanban */
export function KanbanColumnSkeleton({ cards = 3 }) {
    return (
        <div className="flex-shrink-0 w-[clamp(240px,22vw,320px)] min-w-[240px]">
            <div className="rounded-2xl border border-slate-200/60 dark:border-slate-700/40 bg-white/60 dark:bg-slate-900/40 p-3 space-y-3">
                {/* Header de columna */}
                <div className="flex items-center justify-between px-1">
                    <div className="flex items-center gap-2">
                        <Skeleton className="h-4 w-4 rounded" />
                        <Skeleton className="h-4 w-24" />
                    </div>
                    <Skeleton className="h-5 w-8 rounded-full" />
                </div>
                {/* Cards */}
                <div className="space-y-2.5">
                    {Array.from({ length: cards }).map((_, i) => (
                        <LeadCardSkeleton key={i} />
                    ))}
                </div>
            </div>
        </div>
    )
}

/** Skeleton completo del tablero Kanban (banner + action bar + columnas) */
export function KanbanPageSkeleton() {
    return (
        <div className="space-y-3 px-4 py-2 sm:px-6 lg:px-10 xl:px-16 max-w-[1680px] mx-auto">
            {/* Banner skeleton — oculto en mobile/tablet */}
            <div className="hidden lg:block">
                <HeroBannerSkeleton />
            </div>

            {/* Action bar skeleton */}
            <div className="flex items-center justify-between gap-3 px-1">
                <div className="flex items-center gap-2">
                    <Skeleton className="h-8 w-36 rounded-full" />
                    <Skeleton className="h-8 w-20 rounded-full hidden sm:block" />
                </div>
                <Skeleton className="h-9 w-28 rounded-xl" />
            </div>

            {/* Search bar skeleton */}
            <div className="flex items-center gap-2">
                <Skeleton className="h-10 w-72 rounded-xl" />
                <Skeleton className="h-10 w-24 rounded-xl" />
            </div>

            {/* Kanban columns skeleton */}
            <div className="flex gap-3 overflow-hidden pb-4">
                <KanbanColumnSkeleton cards={4} />
                <KanbanColumnSkeleton cards={3} />
                <KanbanColumnSkeleton cards={2} />
                <KanbanColumnSkeleton cards={3} />
                <KanbanColumnSkeleton cards={1} />
                <KanbanColumnSkeleton cards={2} />
            </div>
        </div>
    )
}

/** Skeleton para pantalla completa de carga genérica (ProtectedRoute, HomePage) */
export function FullPageSkeleton({ message }) {
    return (
        <div className="min-h-screen flex items-center justify-center bg-background">
            <div className="flex flex-col items-center gap-6 w-full max-w-sm px-6">
                {/* Logo placeholder */}
                <Skeleton className="h-16 w-16 rounded-2xl" />
                {/* Text lines */}
                <div className="w-full space-y-3">
                    <Skeleton className="h-4 w-3/4 mx-auto" />
                    <Skeleton className="h-3 w-1/2 mx-auto" />
                </div>
                {/* Simulated content blocks */}
                <div className="w-full space-y-2 pt-4">
                    <Skeleton className="h-10 w-full rounded-xl" />
                    <Skeleton className="h-10 w-full rounded-xl" />
                    <Skeleton className="h-8 w-2/3 rounded-xl mx-auto" />
                </div>
                {message && (
                    <p className="text-xs text-muted-foreground animate-pulse">{message}</p>
                )}
            </div>
        </div>
    )
}

/** Skeleton para el panel de admin */
export function AdminPageSkeleton() {
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50/30 dark:from-slate-950 dark:via-slate-900 dark:to-indigo-950/20">
            <div className="mx-auto max-w-[1800px] px-4 py-6 space-y-5">
                {/* Header card skeleton */}
                <div className="relative overflow-hidden rounded-2xl border border-slate-100 bg-white p-5 dark:border-slate-800 dark:bg-slate-900/80">
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700 animate-pulse" />
                    <div className="flex items-center justify-between">
                        <div className="space-y-2">
                            <Skeleton className="h-3 w-36" />
                            <Skeleton className="h-6 w-52" />
                            <Skeleton className="h-3 w-64 hidden sm:block" />
                        </div>
                        <div className="flex items-center gap-2">
                            <Skeleton className="h-9 w-9 rounded-xl" />
                            <Skeleton className="h-9 w-24 rounded-xl" />
                        </div>
                    </div>
                    {/* Filter selects skeleton */}
                    <div className="mt-5 flex flex-wrap gap-3">
                        {[160, 180, 150].map((w, i) => (
                            <div key={i} className="space-y-1.5">
                                <Skeleton className="h-2.5 w-14" />
                                <Skeleton className="h-10 rounded-xl" style={{ width: w }} />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Metric cards skeleton — compact */}
                <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
                    {Array.from({ length: 4 }).map((_, i) => (
                        <div key={i} className="rounded-xl border border-slate-100 bg-white px-3.5 py-3 dark:border-slate-800 dark:bg-slate-900/80 space-y-2">
                            <div className="flex items-center gap-2.5">
                                <Skeleton className="h-8 w-8 rounded-lg flex-shrink-0" />
                                <div className="flex-1 space-y-1">
                                    <Skeleton className="h-5 w-12" />
                                    <Skeleton className="h-2.5 w-20" />
                                </div>
                            </div>
                            <Skeleton className="h-1 w-full rounded-full" />
                        </div>
                    ))}
                </div>

                {/* Tabs skeleton */}
                <Skeleton className="h-12 w-full rounded-2xl" />

                {/* Charts area skeleton */}
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-5">
                    {/* Bar chart area - 3 cols */}
                    <div className="lg:col-span-3 rounded-xl border border-slate-100 bg-white p-4 dark:border-slate-800 dark:bg-slate-900/80 space-y-3">
                        <div className="flex items-center gap-2">
                            <Skeleton className="h-7 w-7 rounded-lg" />
                            <Skeleton className="h-3.5 w-28" />
                        </div>
                        <div className="flex items-end gap-3 h-[200px] pt-4 px-2">
                            {[35, 20, 50, 10, 65, 25, 45].map((h, i) => (
                                <div key={i} className="flex-1 flex flex-col items-center gap-1.5 justify-end h-full">
                                    <Skeleton className="w-full rounded-md" style={{ height: `${h}%` }} />
                                    <Skeleton className="h-2 w-8" />
                                </div>
                            ))}
                        </div>
                    </div>
                    {/* Doughnut area - 2 cols */}
                    <div className="lg:col-span-2 rounded-xl border border-slate-100 bg-white p-4 dark:border-slate-800 dark:bg-slate-900/80 space-y-3">
                        <div className="flex items-center gap-2">
                            <Skeleton className="h-7 w-7 rounded-lg" />
                            <Skeleton className="h-3.5 w-32" />
                        </div>
                        <div className="flex justify-center py-2">
                            <Skeleton className="h-[140px] w-[140px] rounded-full" />
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                            {Array.from({ length: 4 }).map((_, i) => (
                                <Skeleton key={i} className="h-5 w-16 rounded-full" />
                            ))}
                        </div>
                    </div>
                </div>

                {/* Comparison cards skeleton */}
                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-3">
                    {Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="rounded-xl border border-slate-100 bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900/80 space-y-2.5">
                            <div className="flex items-center gap-2.5">
                                <Skeleton className="h-8 w-8 rounded-lg" />
                                <div className="flex-1 space-y-1">
                                    <Skeleton className="h-3.5 w-24" />
                                    <Skeleton className="h-2.5 w-36" />
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                                {Array.from({ length: 3 }).map((_, j) => (
                                    <Skeleton key={j} className="h-11 rounded-lg" />
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}

/** Skeleton para panel de notificaciones */
export function NotificationsSkeleton() {
    return (
        <div className="flex flex-col items-center justify-center py-16 px-4 space-y-4">
            <Skeleton className="h-16 w-16 rounded-full" />
            <Skeleton className="h-4 w-40" />
            <div className="w-full max-w-xs space-y-3 pt-4">
                {Array.from({ length: 3 }).map((_, i) => (
                    <div key={i} className="flex items-start gap-3 rounded-xl border border-slate-100 dark:border-slate-800 p-3">
                        <Skeleton className="h-8 w-8 rounded-lg flex-shrink-0" />
                        <div className="flex-1 space-y-2">
                            <Skeleton className="h-3 w-full" />
                            <Skeleton className="h-2.5 w-2/3" />
                        </div>
                    </div>
                ))}
            </div>
        </div>
    )
}

/** Skeleton para carga de leads en mobile */
export function MobileLeadsSkeleton() {
    return (
        <div className="space-y-3 py-4">
            {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="rounded-xl border border-slate-200/70 dark:border-slate-700/50 bg-white dark:bg-slate-900/60 p-4 space-y-3">
                    <div className="flex items-center gap-3">
                        <Skeleton className="h-10 w-10 rounded-full" />
                        <div className="flex-1 space-y-1.5">
                            <Skeleton className="h-3.5 w-28" />
                            <Skeleton className="h-2.5 w-20" />
                        </div>
                        <Skeleton className="h-6 w-16 rounded-full" />
                    </div>
                    <Skeleton className="h-2.5 w-full" />
                    <div className="flex gap-2">
                        <Skeleton className="h-7 w-20 rounded-lg" />
                        <Skeleton className="h-7 w-20 rounded-lg" />
                    </div>
                </div>
            ))}
        </div>
    )
}

export default Skeleton
